import { PlatformError, validateCuratedSfx, audioSelections, type CuratedSfxSpec, type DocStorePort, type DocTxOps, type DocTxRunner } from '@djgg/core';
import { creationDoc, actionLogDoc } from '../paths.js';
import { isBuildReplaceable, type Creation, type CreationAsset } from '../types.js';
import { AssetRejectionsRepo } from './assetRejections.js';

/** Same parent lock/revision and exact-byte policy as visual attachments. */
export class SfxBindingsRepo {
  constructor(private readonly store: DocStorePort & Partial<DocTxRunner>) {}
  private transaction(): DocTxRunner['runTransaction'] {
    if (!this.store.runTransaction) throw new PlatformError('unavailable', 'sound binding requires server transactions');
    return this.store.runTransaction.bind(this.store);
  }
  async save(input: { creationId: string; spec: CuratedSfxSpec; expectedRevision: number; expectedActiveVersion?: number; actorUid: string; actorHandle: string; atMs: number }) {
    const spec = validateCuratedSfx(input.spec);
    if (!Number.isSafeInteger(input.expectedRevision) || input.expectedRevision < 0) throw new PlatformError('validation', 'Invalid media revision');
    return this.transaction()(async tx => {
      const creation = await tx.get<Creation>(creationDoc(input.creationId));
      if (!creation) throw new PlatformError('not-found', 'Creation not found');
      if (input.expectedActiveVersion !== undefined && (creation.activeVersion ?? 0) !== input.expectedActiveVersion) throw new PlatformError('conflict', 'Active game changed; reload sound choices');
      const revision = creation.assetPolicyRevision ?? 0;
      if (revision !== input.expectedRevision || revision >= Number.MAX_SAFE_INTEGER) throw new PlatformError('conflict', 'Media changed; reload before saving sound');
      const policy = new AssetRejectionsRepo(this.store);
      for (const binding of audioSelections(spec)) if (binding !== 'none') {
        await policy.assertAllowed(input.creationId, binding.contentSha256, revision, tx);
        if (binding.source === 'uploaded-audio-v1') {
          const asset = creation.assets?.find(a => a.variantId === binding.variantId);
          const kind = binding === spec.music ? 'music' : 'sfx';
          if (!asset?.audio || asset.kind !== kind || asset.contentSha256 !== binding.contentSha256) throw new PlatformError('conflict', 'Selected audio changed or is not attached; reload the media library');
        }
      }
      await tx.update(creationDoc(input.creationId), {
        sfxDraft: { spec, actorUid: input.actorUid, updatedAtMs: input.atMs }, assetPolicyRevision: revision + 1,
      });
      await bindAudit(tx, input.creationId, creation, revision + 1, spec, {
        updatedBy: input.actorUid, updatedByHandle: input.actorHandle,
        actionPerformedBy: input.actorUid, actionPerformedByHandle: input.actorHandle, createdAt: input.atMs });
    });
  }

  /** A composed build's go-live retires a generic-era Studio sound draft: the
   * composed soundtrack is the media child's, generated from the game's own
   * brief, so a bound track does not carry onto the composed game. One audited
   * binding step (selected: null) under the publication's creation lock;
   * nothing is written when the game has no draft. */
  async retire(tx: DocTxOps, input: { creationId: string; creation: Creation; actorUid: string; actorHandle: string; atMs: number }): Promise<void> {
    const { creation } = input;
    if (!creation.sfxDraft) return;
    const revision = creation.assetPolicyRevision ?? 0;
    if (revision >= Number.MAX_SAFE_INTEGER) throw new PlatformError('conflict', 'Media revision exhausted');
    await tx.update(creationDoc(input.creationId), { sfxDraft: null, assetPolicyRevision: revision + 1 });
    await bindAudit(tx, input.creationId, creation, revision + 1, null, {
      updatedBy: input.actorUid, updatedByHandle: input.actorHandle,
      actionPerformedBy: input.actorUid, actionPerformedByHandle: input.actorHandle, createdAt: input.atMs });
  }

  /** Atomically replace the build-owned soundtrack, bind it, advance media
   * policy, and write both mutation audits. This is the creation-run path:
   * later iterations must be able to swap a previously bound generated track
   * without an unaudited attach surviving a binding conflict. */
  async attachAndBindGeneratedMusic(input: {
    creationId: string;
    asset: CreationAsset;
    expectedRevision: number;
    expectedActiveVersion: number;
    updatedBy: string;
    updatedByHandle: string;
    actionPerformedBy: string;
    actionPerformedByHandle: string;
    atMs: number;
  }): Promise<{ included: true; assets: CreationAsset[] } | { included: false; preserved: CreationAsset }> {
    if (input.asset.kind !== 'music' || !input.asset.audio || !input.asset.contentSha256) {
      throw new PlatformError('validation', 'Generated soundtrack requires inspected music bytes');
    }
    return this.transaction()(async tx => {
      const creation = await tx.get<Creation>(creationDoc(input.creationId));
      if (!creation) throw new PlatformError('not-found', 'Creation not found');
      if ((creation.activeVersion ?? 0) !== input.expectedActiveVersion) {
        throw new PlatformError('conflict', 'Active game changed; reload sound choices');
      }
      const revision = creation.assetPolicyRevision ?? 0;
      if (revision !== input.expectedRevision || revision >= Number.MAX_SAFE_INTEGER) {
        throw new PlatformError('conflict', 'Media changed; reload before saving sound');
      }
      await new AssetRejectionsRepo(this.store).assertAllowed(
        input.creationId,
        input.asset.contentSha256,
        revision,
        tx,
      );

      const assets = creation.assets ?? [];
      const existingById = assets.find(asset => asset.variantId === input.asset.variantId);
      const priorIndex = assets.findIndex(asset => (asset.name ?? '').toLowerCase() === input.asset.name.toLowerCase());
      const prior = priorIndex < 0 ? null : assets[priorIndex];
      if (prior && prior.variantId !== input.asset.variantId && !isBuildReplaceable(prior)) {
        return { included: false as const, preserved: prior };
      }
      if (prior && prior.variantId !== input.asset.variantId && prior.kind !== 'music') {
        throw new PlatformError('conflict', `Soundtrack name is already attached as ${prior.kind}`);
      }

      const nextAssets = assets.slice();
      if (!existingById) {
        if (priorIndex < 0) nextAssets.push(input.asset);
        else nextAssets[priorIndex] = input.asset;
      }
      const spec = validateCuratedSfx({
        schemaVersion: 2,
        cues: { ...(creation.sfxDraft?.spec.cues ?? {}) },
        music: {
          variantId: input.asset.variantId,
          source: 'uploaded-audio-v1',
          contentSha256: input.asset.contentSha256,
        },
      });
      for (const binding of audioSelections(spec)) if (binding !== 'none') {
        await new AssetRejectionsRepo(this.store).assertAllowed(
          input.creationId,
          binding.contentSha256,
          revision,
          tx,
        );
        if (binding.source === 'uploaded-audio-v1') {
          const attached = nextAssets.find(asset => asset.variantId === binding.variantId);
          const kind = binding === spec.music ? 'music' : 'sfx';
          if (!attached?.audio || attached.kind !== kind || attached.contentSha256 !== binding.contentSha256) {
            throw new PlatformError('conflict', 'Selected audio changed or is not attached; reload the media library');
          }
        }
      }

      await tx.update(creationDoc(input.creationId), {
        assets: nextAssets,
        sfxDraft: { spec, actorUid: input.updatedBy, updatedAtMs: input.atMs },
        assetPolicyRevision: revision + 1,
        updatedAt: this.store.serverTimestamp(),
      });
      const auditBase = {
        updatedBy: input.updatedBy,
        updatedByHandle: input.updatedByHandle,
        actionPerformedBy: input.actionPerformedBy,
        actionPerformedByHandle: input.actionPerformedByHandle,
        createdAt: input.atMs,
      };
      if (!existingById) {
        const attachPath = actionLogDoc(`creation-music-attach-${input.creationId}-${revision + 1}`);
        if (await tx.get(attachPath)) throw new PlatformError('conflict', 'Soundtrack attachment audit already exists');
        await tx.set(attachPath, {
          ...auditBase,
          creationId: input.creationId,
          slug: creation.slug,
          title: creation.title,
          action: 'asset.attach',
          details: {
            variantId: input.asset.variantId,
            kind: 'music',
            name: input.asset.name,
            ...(prior ? { replacedVariantId: prior.variantId } : {}),
          },
        });
      }
      await bindAudit(tx, input.creationId, creation, revision + 1, spec, auditBase);
      return { included: true as const, assets: nextAssets };
    });
  }
}

/** Who a sound-binding audit row credits (`updatedBy`) and who performed it. */
interface BindAuditActor {
  updatedBy: string;
  updatedByHandle: string;
  actionPerformedBy: string;
  actionPerformedByHandle: string;
  createdAt: number;
}

/** The ONE `media.sfxBind` audit row of a sound-binding step: one per media
 * revision (`sfx-<creation>-<revision>`), from the draft it replaced to the
 * selection it saved (null when the draft was retired). */
async function bindAudit(tx: DocTxOps, creationId: string, creation: Creation, revision: number, selected: CuratedSfxSpec | null, actor: BindAuditActor): Promise<void> {
  const auditPath = actionLogDoc(`sfx-${creationId}-${revision}`);
  if (await tx.get(auditPath)) throw new PlatformError('conflict', 'Sound audit revision already exists');
  await tx.set(auditPath, { ...actor, action: 'media.sfxBind', creationId, slug: creation.slug, title: creation.title,
    details: { revision, previous: creation.sfxDraft?.spec ?? null, selected } });
}
